// CalendarView — the Calendar page (tab 26, /calendar). Lead times &
// expected deliveries for ordered items, alongside the Tracker.
//
// Built 2026-07-12 from the Claude Design handoff (design_handoff_gilda_
// calendar). Three zoom levels (Month / Week / Quarter), persistent
// overdue rail, undated strip, item popover, select & isolate.
//
// Two independent visual axes, per the design:
//   COLOR = timing  (green on-track / amber due-soon / red overdue /
//                    grey delivered) — dots + text only, never fills.
//   ICON  = location (production ◇ / transit → / receiver ⊡ /
//                    install-sched ▤ / installed ✓). Transit and
//                    install-scheduled render in the legend but have no
//                    backing data yet (no shippedDate / installDate
//                    fields) — see itemLocationState in utils.js.
//
// All derivation is pure and lives in utils.js (buildDeliverySchedule,
// itemDeliveryTiming, calendarMonthGrid, …) — this file is presentation
// + interaction state only. Data comes from useAppContext(); never
// fetched here.

import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from './AppContext';
import {
  buildDeliverySchedule, buildChaseListText, itemLocationState,
  calendarMonthGrid, startOfIsoWeek, addDaysIso, isoWeekNumber,
  STATUSES,
  CAL_ORDERED_STATUSES,
} from '../utils';
import {
  INK_1, INK_2, INK_3, INK_4, SURFACE, GROUND_OFF,
  ZONE_GREEN, ZONE_FUCHSIA, ZONE_GREY,
  ALERT_RED_TEXT, ALERT_AMBER_TEXT, ALERT_AMBER_BORDER,
  HAIRLINE, HAIRLINE_ROW, HAIRLINE_SHELL, HAIRLINE_THICK_FUCHSIA,
  TYPE_EYEBROW, FONT_FAMILY, TABULAR, SHADOW_MODAL, Z_MODAL,
} from '../lib/designTokens';

// ── timing → visual mapping (dot + text, never fills) ─────────────────
var TIMING_STYLE = {
  onTrack:   { dot: ZONE_GREEN,      text: INK_1,           weight: 400, label: 'On track' },
  dueSoon:   { dot: ALERT_AMBER_BORDER, text: ALERT_AMBER_TEXT, weight: 500, label: 'Due soon' },
  overdue:   { dot: ALERT_RED_TEXT,  text: ALERT_RED_TEXT,  weight: 500, label: 'Overdue' },
  delivered: { dot: ZONE_GREY,       text: INK_3,           weight: 400, label: 'Delivered' },
};

var LOCATION_LABEL = {
  production: 'In production',
  transit: 'In transit',
  receiver: 'At receiver',
  installScheduled: 'Install scheduled',
  installed: 'Installed',
};

// 10px monochrome line icons from the design handoff (geometric, stroke ~1.1).
function LocIcon(props) {
  var c = props.color || INK_2;
  var s = { flex: 'none' };
  switch (props.state) {
    case 'production': return (
      <svg width="10" height="10" viewBox="0 0 10 10" style={s}>
        <rect x="2.3" y="2.3" width="5.4" height="5.4" fill="none" stroke={c} strokeWidth="1.1" transform="rotate(45 5 5)" />
      </svg>);
    case 'transit': return (
      <svg width="10" height="10" viewBox="0 0 10 10" style={s}>
        <path d="M1.5 5h6" stroke={c} strokeWidth="1.1" fill="none" />
        <path d="M5.2 2.4 7.8 5 5.2 7.6" stroke={c} strokeWidth="1.1" fill="none" />
      </svg>);
    case 'receiver': return (
      <svg width="10" height="10" viewBox="0 0 10 10" style={s}>
        <rect x="1.7" y="1.7" width="6.6" height="6.6" fill="none" stroke={c} strokeWidth="1.1" />
        <circle cx="5" cy="5" r="1.3" fill={c} />
      </svg>);
    case 'installScheduled': return (
      <svg width="10" height="10" viewBox="0 0 10 10" style={s}>
        <rect x="1.7" y="2.2" width="6.6" height="6.1" fill="none" stroke={c} strokeWidth="1.1" />
        <path d="M1.7 4.4h6.6" stroke={c} strokeWidth="1.1" />
      </svg>);
    default: return ( // installed ✓
      <svg width="10" height="10" viewBox="0 0 10 10" style={s}>
        <path d="M2 5.4 4.3 7.6 8 2.6" fill="none" stroke={c} strokeWidth="1.2" />
      </svg>);
  }
}

function Dot(props) {
  var sz = props.size || 7;
  return <span style={{ width: sz, height: sz, borderRadius: '50%', background: props.color, display: 'inline-block', flexShrink: 0 }} />;
}

// ── local date display helpers ─────────────────────────────────────────
function parseLocal(iso) {
  var m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  var d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return isNaN(d.getTime()) ? null : d;
}
function fmtShort(iso) { // "3 Jul"
  var d = parseLocal(iso);
  return d ? d.getDate() + ' ' + d.toLocaleDateString('en-US', { month: 'short' }) : '—';
}
function fmtLong(iso) { // "3 Jul 2026"
  var d = parseLocal(iso);
  return d ? d.getDate() + ' ' + d.toLocaleDateString('en-US', { month: 'short' }) + ' ' + d.getFullYear() : '—';
}
function leadLabel(item) {
  if (item.leadWeeks) return item.leadWeeks + ' wks';
  return item.leadTime || '—';
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  var ta = document.createElement('textarea');
  ta.value = text; document.body.appendChild(ta); ta.select();
  document.execCommand('copy'); document.body.removeChild(ta);
  return Promise.resolve();
}

// ── MAIN ───────────────────────────────────────────────────────────────
export default function CalendarView(p) {
  var ctx = useAppContext();
  var items = ctx.items || [];
  var projects = ctx.projects || [];
  var updateItem = ctx.updateItem;
  var onNavigate = p.onNavigate;

  var now = new Date();
  var todayIso = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');

  // ── state ────────────────────────────────────────────────────────────
  var vs = useState('month'); var view = vs[0]; var setView = vs[1];
  var as = useState(todayIso); var anchor = as[0]; var setAnchor = as[1];
  var fps = useState(''); var fProject = fps[0]; var setFProject = fps[1];
  var fvs = useState(''); var fVendor = fvs[0]; var setFVendor = fvs[1];
  var fss = useState('all'); var fStatus = fss[0]; var setFStatus = fss[1];
  var sels = useState([]); var selection = sels[0]; var setSelection = sels[1];
  var isos = useState(false); var isolated = isos[0]; var setIsolated = isos[1];
  var pops = useState(null); var popover = pops[0]; var setPopover = pops[1];
  var qhs = useState(null); var qHover = qhs[0]; var setQHover = qhs[1];
  var uds = useState(false); var undatedOpen = uds[0]; var setUndatedOpen = uds[1];
  var cps = useState(''); var copied = cps[0]; var setCopied = cps[1];

  // ── derived schedule ─────────────────────────────────────────────────
  var scoped = useMemo(function() {
    return items.filter(function(it) {
      if (!it) return false;
      if (fProject && it.projectLabel !== fProject && it.project !== fProject) return false;
      if (fVendor && !(it.vendor && it.vendor.toLowerCase().indexOf(fVendor.toLowerCase()) >= 0)) return false;
      return true;
    });
  }, [items, fProject, fVendor]);

  var schedule = useMemo(function() {
    return buildDeliverySchedule(scoped, todayIso);
  }, [scoped, todayIso]);

  // Status-chip filter applies to plotted events (the rail stays global).
  var visibleEvents = useMemo(function() {
    return schedule.events.filter(function(ev) {
      if (fStatus === 'onorder') return ev.timing !== 'delivered';
      if (fStatus === 'overdue') return ev.timing === 'overdue';
      if (fStatus === 'delivered') return ev.timing === 'delivered';
      return true;
    });
  }, [schedule, fStatus]);

  var eventsByDay = useMemo(function() {
    var map = {};
    visibleEvents.forEach(function(ev) { (map[ev.dateIso] = map[ev.dateIso] || []).push(ev); });
    Object.keys(map).forEach(function(k) {
      map[k].sort(function(a, b) { return b.daysLate - a.daysLate; });
    });
    return map;
  }, [visibleEvents]);

  // ── period math ──────────────────────────────────────────────────────
  var anchorD = parseLocal(anchor) || now;
  var aYear = anchorD.getFullYear(), aMonth = anchorD.getMonth() + 1;
  var weekStart = startOfIsoWeek(anchor);
  var weekDays = []; for (var i = 0; i < 7; i++) weekDays.push(addDaysIso(weekStart, i));
  var qStartMonth = Math.floor((aMonth - 1) / 3) * 3 + 1;

  function stepPeriod(dir) {
    if (view === 'week') { setAnchor(addDaysIso(anchor, 7 * dir)); return; }
    var months = view === 'quarter' ? 3 * dir : dir;
    var d = new Date(aYear, aMonth - 1 + months, 1);
    setAnchor(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-01');
  }

  var MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var periodLabel = view === 'month' ? MONTH_NAMES[aMonth - 1] + ' ' + aYear
    : view === 'week' ? 'Week ' + isoWeekNumber(weekStart) + ' · ' + fmtShort(weekStart) + ' – ' + fmtLong(addDaysIso(weekStart, 6))
    : 'Q' + (Math.floor((aMonth - 1) / 3) + 1) + ' ' + aYear + ' · ' + MONTH_NAMES[qStartMonth - 1] + ' – ' + MONTH_NAMES[qStartMonth + 1];

  // Events inside the displayed period (footer count).
  var periodRange = view === 'month'
    ? [aYear + '-' + String(aMonth).padStart(2, '0') + '-01', new Date(aYear, aMonth, 0)]
    : view === 'week' ? [weekStart, parseLocal(addDaysIso(weekStart, 6))]
    : [aYear + '-' + String(qStartMonth).padStart(2, '0') + '-01', new Date(aYear, qStartMonth + 2, 0)];
  var periodEndIso = periodRange[1].getFullYear ? (function(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  })(periodRange[1]) : periodRange[1];
  var inPeriod = visibleEvents.filter(function(ev) { return ev.dateIso >= periodRange[0] && ev.dateIso <= periodEndIso; });
  var periodNoun = view === 'month' ? 'this month' : view === 'week' ? 'this week' : 'this quarter';

  // ── selection ────────────────────────────────────────────────────────
  function isSelected(id) { return selection.indexOf(id) >= 0; }
  function dimmed(id) { return isolated && selection.length > 0 && !isSelected(id); }

  function chipClick(e, ev) {
    e.stopPropagation();
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      setSelection(isSelected(ev.id)
        ? selection.filter(function(x) { return x !== ev.id; })
        : selection.concat([ev.id]));
      setPopover(null);
      return;
    }
    setSelection([ev.id]);
    var r = e.currentTarget.getBoundingClientRect();
    var x = Math.min(r.left, window.innerWidth - 340);
    var y = r.bottom + 6 + 320 > window.innerHeight ? Math.max(8, r.top - 326) : r.bottom + 6;
    setPopover({ id: ev.id, x: x, y: y });
  }

  function clearAll() { setSelection([]); setIsolated(false); setPopover(null); }

  function flashCopied(tag) {
    setCopied(tag);
    setTimeout(function() { setCopied(''); }, 1600);
  }

  // ── keyboard: ←/→ period, T today, Esc clear ─────────────────────────
  useEffect(function() {
    function onKey(e) {
      var tag = (e.target && e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'ArrowLeft') stepPeriod(-1);
      else if (e.key === 'ArrowRight') stepPeriod(1);
      else if (e.key === 't' || e.key === 'T') setAnchor(todayIso);
      else if (e.key === 'Escape') { if (popover) setPopover(null); else clearAll(); }
    }
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  });

  // ── actions ──────────────────────────────────────────────────────────
  function markReceived(item) {
    if (!updateItem) return;
    // Forward-only (2026-07-29): this popover action must never walk a
    // Delivered/Installed item back to At Receiver.
    var cur = STATUSES.indexOf(item.status);
    if (cur >= STATUSES.indexOf('At Receiver')) return;
    // patch-only — full-row writes can resurrect stale fields
    updateItem(item.id, { status: 'At Receiver', receivedDate: todayIso });
    setPopover(null);
  }

  function copyChaseList() {
    copyText(buildChaseListText(schedule.overdue)).then(function() { flashCopied('chase'); });
  }

  function copySelectionList() {
    var evs = schedule.events.filter(function(ev) { return isSelected(ev.id); });
    copyText(buildChaseListText(evs)).then(function() { flashCopied('selection'); });
  }

  function exportSelectionCSV() {
    var evs = schedule.events.filter(function(ev) { return isSelected(ev.id); });
    var hd = ['Item', 'Vendor', 'Project', 'Room', 'PO', 'Status', 'Ordered', 'Lead', 'Expected', 'Timing', 'Received'];
    var rows = evs.map(function(ev) {
      var it = ev.item;
      return [it.productName || it.item, it.vendor || '', it.projectLabel || it.project || '',
        it.roomName || '', it.poNumber || '', it.status || '', it.orderPlacedDate || '',
        leadLabel(it), ev.dateIso || '', TIMING_STYLE[ev.timing].label, it.receivedDate || ''];
    });
    var csv = [hd].concat(rows).map(function(r) { return r.map(function(c) { return '"' + (c || '') + '"'; }).join(','); }).join('\n');
    var a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'calendar-' + todayIso + '.csv'; a.click();
  }

  // ── shared little styles ─────────────────────────────────────────────
  var ghostBtn = { height: 26, padding: '0 12px', borderRadius: 6, border: HAIRLINE, background: SURFACE,
    fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', color: INK_1, whiteSpace: 'nowrap' };
  var eyebrow = Object.assign({}, TYPE_EYEBROW);

  // ── chip renderers ───────────────────────────────────────────────────
  function monthChip(ev) {
    var t = TIMING_STYLE[ev.timing];
    var sel = isSelected(ev.id);
    return (
      <div key={ev.id} onClick={function(e) { chipClick(e, ev); }}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 6px', borderRadius: 4,
          background: SURFACE, cursor: 'pointer', marginBottom: 3,
          border: sel ? HAIRLINE_THICK_FUCHSIA : (ev.timing === 'dueSoon' ? '0.5px solid ' + ALERT_AMBER_BORDER : HAIRLINE),
          opacity: dimmed(ev.id) ? 0.25 : 1 }}>
        <Dot color={t.dot} />
        <LocIcon state={itemLocationState(ev.item)} color={INK_2} />
        <span style={{ fontSize: 11, fontWeight: t.weight, color: t.text, whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
          {ev.item.productName || ev.item.item}
        </span>
        {ev.timing === 'delivered' && (
          <svg width="9" height="9" viewBox="0 0 10 10" style={{ flex: 'none' }}>
            <path d="M2 5.4 4.3 7.6 8 2.6" fill="none" stroke={INK_3} strokeWidth="1.2" />
          </svg>
        )}
      </div>
    );
  }

  function weekChip(ev) {
    var t = TIMING_STYLE[ev.timing];
    var it = ev.item;
    var sel = isSelected(ev.id);
    return (
      <div key={ev.id} onClick={function(e) { chipClick(e, ev); }}
        style={{ padding: '6px 8px', borderRadius: 4, background: SURFACE, cursor: 'pointer', marginBottom: 4,
          border: sel ? HAIRLINE_THICK_FUCHSIA : (ev.timing === 'dueSoon' ? '0.5px solid ' + ALERT_AMBER_BORDER : HAIRLINE),
          opacity: dimmed(ev.id) ? 0.25 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Dot color={t.dot} />
          <LocIcon state={itemLocationState(it)} color={INK_2} />
          <span style={{ fontSize: 11, fontWeight: 500, color: t.text, whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
            {it.productName || it.item}
          </span>
          {ev.timing === 'delivered' && (
            <svg width="9" height="9" viewBox="0 0 10 10" style={{ flex: 'none' }}>
              <path d="M2 5.4 4.3 7.6 8 2.6" fill="none" stroke={INK_3} strokeWidth="1.2" />
            </svg>
          )}
        </div>
        <div style={{ fontSize: 10, color: INK_3, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflowEllipsis: 'ellipsis' }}>
          {(it.projectLabel || it.project || '—')}{it.roomName ? ' · ' + it.roomName : ''}{it.poNumber ? ' · ' + it.poNumber : ''}
        </div>
        {it.orderPlacedDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', border: '1px solid ' + INK_3, flexShrink: 0 }} />
            <span style={{ flex: 1, borderTop: HAIRLINE_SHELL, minWidth: 12 }} />
            <Dot color={t.dot} size={5} />
            <span style={{ fontSize: 9, color: INK_3, whiteSpace: 'nowrap', fontFeatureSettings: TABULAR }}>
              ord {fmtShort(it.orderPlacedDate)} · {leadLabel(it)}
            </span>
          </div>
        )}
        {ev.timing === 'overdue' && (
          <div style={{ fontSize: 10, color: ALERT_RED_TEXT, fontWeight: 500, marginTop: 3 }}>
            {ev.daysLate} day{ev.daysLate === 1 ? '' : 's'} late · {LOCATION_LABEL[itemLocationState(it)].toLowerCase()}
          </div>
        )}
      </div>
    );
  }

  // ── views ────────────────────────────────────────────────────────────
  var WEEKDAY_EYEBROWS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

  function monthView() {
    var weeks = calendarMonthGrid(aYear, aMonth);
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: HAIRLINE_ROW }}>
          {WEEKDAY_EYEBROWS.map(function(d, di) {
            return <div key={d} style={Object.assign({}, eyebrow, { padding: '6px 8px',
              color: di >= 5 ? INK_4 : INK_3 })}>{d}</div>;
          })}
        </div>
        {weeks.map(function(w) {
          return (
            <div key={w.isoWeek} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {w.days.map(function(day, di) {
                var evs = eventsByDay[day.iso] || [];
                var isToday = day.iso === todayIso;
                var shown = evs.slice(0, 2);
                var extra = evs.length - shown.length;
                return (
                  <div key={day.iso}
                    style={{ minHeight: 112, padding: '6px 8px', position: 'relative',
                      borderRight: di < 6 ? HAIRLINE_ROW : 'none', borderBottom: HAIRLINE_ROW,
                      borderTop: isToday ? HAIRLINE_THICK_FUCHSIA : '1.5px solid transparent',
                      background: (!day.inMonth || day.weekend) ? GROUND_OFF : SURFACE }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontFeatureSettings: TABULAR,
                        fontWeight: isToday ? 500 : 400,
                        color: isToday ? ZONE_FUCHSIA : day.inMonth ? INK_2 : INK_4 }}>
                        {day.day}
                      </span>
                      {di === 0 && <span style={{ fontSize: 9, color: INK_4, fontFeatureSettings: TABULAR }}>W{w.isoWeek}</span>}
                      {isToday && <span style={Object.assign({}, eyebrow, { color: ZONE_FUCHSIA, fontSize: 9 })}>Today</span>}
                    </div>
                    {shown.map(monthChip)}
                    {extra > 0 && (
                      <div onClick={function() { setAnchor(day.iso); setView('week'); }}
                        style={{ fontSize: 10, color: INK_3, cursor: 'pointer', padding: '1px 2px' }}>
                        + {extra} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  function weekView() {
    var weekEvents = visibleEvents.filter(function(ev) { return weekDays.indexOf(ev.dateIso) >= 0; });
    var vendorNames = [];
    weekEvents.forEach(function(ev) {
      var v = ev.item.vendor || 'No vendor';
      if (vendorNames.indexOf(v) < 0) vendorNames.push(v);
    });
    vendorNames.sort();
    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '170px repeat(7, 1fr)', borderBottom: HAIRLINE_ROW }}>
          <div style={Object.assign({}, eyebrow, { padding: '6px 10px' })}>Vendor</div>
          {weekDays.map(function(iso, di) {
            var isToday = iso === todayIso;
            return (
              <div key={iso} style={Object.assign({}, eyebrow, { padding: '6px 8px',
                color: isToday ? ZONE_FUCHSIA : di >= 5 ? INK_4 : INK_3 })}>
                {WEEKDAY_EYEBROWS[di]} {parseLocal(iso).getDate()}{isToday ? ' · TODAY' : ''}
              </div>
            );
          })}
        </div>
        {vendorNames.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 12, color: INK_4 }}>
            No expected deliveries this week.
          </div>
        )}
        {vendorNames.map(function(vname) {
          var laneItems = scoped.filter(function(it) { return (it.vendor || 'No vendor') === vname; });
          var openPOs = [];
          laneItems.forEach(function(it) {
            if (CAL_ORDERED_STATUSES.indexOf(String(it.status || '').trim()) >= 0 && it.poNumber && openPOs.indexOf(it.poNumber) < 0) openPOs.push(it.poNumber);
          });
          var laneOverdue = schedule.overdue.filter(function(ev) { return (ev.item.vendor || 'No vendor') === vname; }).length;
          return (
            <div key={vname} style={{ display: 'grid', gridTemplateColumns: '170px repeat(7, 1fr)', borderBottom: HAIRLINE_ROW }}>
              <div style={{ padding: '8px 10px', borderRight: HAIRLINE_ROW }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: INK_1 }}>{vname}</div>
                <div style={{ fontSize: 10, color: INK_3, marginTop: 2 }}>
                  {openPOs.length} open PO{openPOs.length === 1 ? '' : 's'}{laneOverdue > 0 ? ' · ' + laneOverdue + ' overdue' : ''}
                </div>
              </div>
              {weekDays.map(function(iso, di) {
                var evs = (eventsByDay[iso] || []).filter(function(ev) { return (ev.item.vendor || 'No vendor') === vname; });
                var isToday = iso === todayIso;
                return (
                  <div key={iso} style={{ minHeight: 96, padding: '6px 6px',
                    borderRight: di < 6 ? HAIRLINE_ROW : 'none',
                    borderTop: isToday ? HAIRLINE_THICK_FUCHSIA : '1.5px solid transparent',
                    background: di >= 5 ? GROUND_OFF : SURFACE }}>
                    {evs.map(weekChip)}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{ padding: '7px 14px', fontSize: 10, color: INK_3, borderBottom: HAIRLINE_ROW }}>
          ◦ order placed &nbsp;·&nbsp; ─ lead time &nbsp;·&nbsp; ● expected delivery
        </div>
      </div>
    );
  }

  function quarterView() {
    var minis = [0, 1, 2].map(function(off) {
      var m = qStartMonth + off;
      return { year: aYear, month: m, weeks: calendarMonthGrid(aYear, m) };
    });
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {minis.map(function(mini, mi) {
          var mPrefix = mini.year + '-' + String(mini.month).padStart(2, '0');
          var mEvents = visibleEvents.filter(function(ev) { return ev.dateIso.slice(0, 7) === mPrefix; });
          var mOverdue = mEvents.filter(function(ev) { return ev.timing === 'overdue'; }).length;
          return (
            <div key={mini.month} style={{ padding: '10px 14px', borderRight: mi < 2 ? HAIRLINE_ROW : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={eyebrow}>{MONTH_NAMES[mini.month - 1]}</span>
                <span style={{ fontSize: 10, color: INK_3, fontFeatureSettings: TABULAR }}>
                  {mEvents.length} deliveries{mOverdue > 0 ? ' · ' : ''}
                  {mOverdue > 0 && <span style={{ color: ALERT_RED_TEXT, fontWeight: 500 }}>{mOverdue} overdue</span>}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map(function(d, di) {
                  return <div key={di} style={{ fontSize: 9, color: di >= 5 ? INK_4 : INK_3, textAlign: 'center', padding: '2px 0' }}>{d}</div>;
                })}
                {mini.weeks.map(function(w) {
                  return w.days.map(function(day) {
                    if (!day.inMonth) return <div key={day.iso} />;
                    var evs = eventsByDay[day.iso] || [];
                    var worst = evs.reduce(function(acc, ev) {
                      var rank = { overdue: 3, dueSoon: 2, onTrack: 1, delivered: 0 };
                      return rank[ev.timing] > rank[acc] ? ev.timing : acc;
                    }, 'delivered');
                    var isToday = day.iso === todayIso;
                    var numColor = isToday ? ZONE_FUCHSIA
                      : evs.length && worst === 'overdue' ? ALERT_RED_TEXT
                      : evs.length && worst === 'dueSoon' ? ALERT_AMBER_TEXT
                      : day.weekend ? INK_4 : INK_2;
                    return (
                      <div key={day.iso}
                        onClick={function() { if (evs.length) { setAnchor(day.iso); setView('month'); } }}
                        onMouseEnter={function(e) {
                          if (!evs.length) return;
                          var r = e.currentTarget.getBoundingClientRect();
                          setQHover({ iso: day.iso, x: Math.min(r.left, window.innerWidth - 260), y: r.bottom + 4 });
                        }}
                        onMouseLeave={function() { setQHover(null); }}
                        style={{ height: 40, textAlign: 'center', cursor: evs.length ? 'pointer' : 'default', paddingTop: 3 }}>
                        <div style={{ fontSize: 10, fontFeatureSettings: TABULAR,
                          fontWeight: (isToday || (evs.length && (worst === 'overdue' || worst === 'dueSoon'))) ? 500 : 400,
                          color: numColor,
                          borderBottom: isToday ? '1.5px solid ' + ZONE_FUCHSIA : 'none',
                          display: 'inline-block', minWidth: 14 }}>
                          {day.day}
                        </div>
                        <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 2 }}>
                          {evs.slice(0, 3).map(function(ev) {
                            return <span key={ev.id} style={{ width: 6, height: 6, borderRadius: '50%',
                              background: TIMING_STYLE[ev.timing].dot,
                              opacity: dimmed(ev.id) ? 0.25 : 1 }} />;
                          })}
                          {evs.length > 3 && <span style={{ fontSize: 8, color: INK_3 }}>+{evs.length - 3}</span>}
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── quarter hover tooltip ────────────────────────────────────────────
  var qTooltip = null;
  if (qHover && view === 'quarter') {
    var tipEvs = eventsByDay[qHover.iso] || [];
    qTooltip = (
      <div style={{ position: 'fixed', left: qHover.x, top: qHover.y, width: 236, background: SURFACE,
        border: HAIRLINE, borderRadius: 6, boxShadow: SHADOW_MODAL, zIndex: Z_MODAL, padding: '8px 10px', pointerEvents: 'none' }}>
        <div style={Object.assign({}, eyebrow, { marginBottom: 5 })}>
          {fmtLong(qHover.iso)} · {tipEvs.length} item{tipEvs.length === 1 ? '' : 's'}
        </div>
        {tipEvs.slice(0, 6).map(function(ev) {
          var t = TIMING_STYLE[ev.timing];
          return (
            <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 0' }}>
              <Dot color={t.dot} size={6} />
              <span style={{ fontSize: 11, color: t.text, fontWeight: t.weight, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {ev.item.productName || ev.item.item}
              </span>
              <span style={{ fontSize: 10, color: INK_3, whiteSpace: 'nowrap' }}>· {ev.item.vendor || '—'}</span>
            </div>
          );
        })}
        <div style={{ fontSize: 9, color: INK_4, marginTop: 5 }}>Click to open in Month view</div>
      </div>
    );
  }

  // ── item popover ─────────────────────────────────────────────────────
  var popoverEl = null;
  if (popover) {
    var pev = schedule.events.find(function(ev) { return ev.id === popover.id; });
    if (pev) {
      var pit = pev.item;
      var pt = TIMING_STYLE[pev.timing];
      var loc = itemLocationState(pit);
      var rows = [
        ['Project', pit.projectLabel || pit.project || '—', null],
        ['Room', (pit.roomNumber ? pit.roomNumber + ' · ' : '') + (pit.roomName || '—'), null],
        ['Sidemark', pit.sidemark || '—', null],
        ['Order placed', pit.orderPlacedDate ? fmtLong(pit.orderPlacedDate) : '—', null],
        ['Lead time', leadLabel(pit), null],
        ['Expected', pev.timing !== 'delivered' ? fmtLong(pev.dateIso) : (pit.orderPlacedDate ? '—' : '—'), pev.timing === 'overdue' ? ALERT_RED_TEXT : null],
        ['Received', pit.receivedDate ? fmtLong(pit.receivedDate) : '—', null],
        ['Install', pit.installDate ? fmtLong(pit.installDate) : '—', null],
      ];
      popoverEl = (
        <div onClick={function(e) { e.stopPropagation(); }}
          style={{ position: 'fixed', left: popover.x, top: popover.y, width: 312, background: SURFACE,
            border: HAIRLINE, borderRadius: 6, boxShadow: SHADOW_MODAL, zIndex: Z_MODAL, padding: '12px 14px' }}>
          <div style={Object.assign({}, eyebrow, { marginBottom: 3 })}>
            {(pit.poNumber || 'No PO')} · {(pit.vendor || 'No vendor')}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: INK_1, marginBottom: 4 }}>
            {pit.productName || pit.item}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
            <Dot color={pt.dot} />
            <span style={{ fontSize: 11, fontWeight: 500, color: pt.text }}>
              {pt.label}{pev.timing === 'overdue' ? ' · ' + pev.daysLate + ' days late' : ''}
            </span>
            <span style={{ fontSize: 11, color: INK_3 }}>·</span>
            <LocIcon state={loc} color={INK_2} />
            <span style={{ fontSize: 11, color: INK_2 }}>{LOCATION_LABEL[loc]}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', rowGap: 5, marginBottom: 12 }}>
            {rows.map(function(r) {
              return [
                <div key={r[0] + 'l'} style={eyebrow}>{r[0]}</div>,
                <div key={r[0] + 'v'} style={{ fontSize: 11, fontFeatureSettings: TABULAR,
                  color: r[2] || (r[1] === '—' ? INK_4 : INK_1), fontWeight: r[2] ? 500 : 400 }}>{r[1]}</div>,
              ];
            })}
          </div>
          {pit.orderPlacedDate && pev.timing !== 'delivered' && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', border: '1px solid ' + INK_3, flexShrink: 0 }} />
                <span style={{ flex: 2, borderTop: HAIRLINE_SHELL }} />
                <Dot color={pt.dot} size={6} />
                {pev.timing === 'overdue' && [
                  <span key="dash" style={{ flex: 1, borderTop: '1px dashed ' + ALERT_RED_TEXT }} />,
                  <span key="tick" style={{ width: 1.5, height: 10, background: ZONE_FUCHSIA, flexShrink: 0 }} />,
                ]}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: INK_3, marginTop: 3, fontFeatureSettings: TABULAR }}>
                <span>{fmtShort(pit.orderPlacedDate)} ordered</span>
                <span style={{ color: pev.timing === 'overdue' ? ALERT_RED_TEXT : INK_3 }}>
                  {fmtShort(pev.dateIso)} expected
                </span>
                {pev.timing === 'overdue' && <span style={{ color: ZONE_FUCHSIA }}>today</span>}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={function() {
              copyText(buildChaseListText([pev])).then(function() { flashCopied('chase-one'); });
            }}
              style={{ padding: '7px 18px', borderRadius: 6, fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                background: INK_1, color: SURFACE, border: '1px solid ' + INK_1, cursor: 'pointer' }}>
              {copied === 'chase-one' ? 'Copied ✓' : 'Chase vendor'}
            </button>
            {pit.poNumber && onNavigate && (
              <button onClick={function() { onNavigate('/purchase-orders'); }}
                style={{ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                  background: SURFACE, color: INK_1, border: '1px solid ' + INK_1, cursor: 'pointer' }}>
                Open {pit.poNumber}
              </button>
            )}
            {pev.timing !== 'delivered' && (
              <button onClick={function() { markReceived(pit); }}
                style={{ padding: '7px 14px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit',
                  background: SURFACE, color: INK_2, border: HAIRLINE, cursor: 'pointer' }}>
                Mark received
              </button>
            )}
          </div>
        </div>
      );
    }
  }

  // ── overdue rail ─────────────────────────────────────────────────────
  var overdueRail = schedule.overdue.length > 0 && fStatus !== 'delivered' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderBottom: HAIRLINE_SHELL, overflowX: 'auto' }}>
      <span style={Object.assign({}, eyebrow, { color: ALERT_RED_TEXT, whiteSpace: 'nowrap' })}>
        Overdue · {schedule.overdue.length}
      </span>
      {schedule.overdue.map(function(ev) {
        var it = ev.item;
        return (
          <div key={ev.id}
            onClick={function(e) { setAnchor(ev.dateIso); chipClick(e, ev); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 4,
              border: isSelected(ev.id) ? HAIRLINE_THICK_FUCHSIA : HAIRLINE, cursor: 'pointer', whiteSpace: 'nowrap',
              opacity: dimmed(ev.id) ? 0.25 : 1, background: SURFACE }}>
            <Dot color={ALERT_RED_TEXT} />
            <span style={{ fontSize: 11, fontWeight: 500, color: ALERT_RED_TEXT }}>{it.productName || it.item}</span>
            <span style={{ fontSize: 10, color: INK_3 }}>
              {it.vendor || '—'}{it.poNumber ? ' · ' + it.poNumber : ''} · exp {fmtShort(ev.dateIso)}
            </span>
            <span style={{ fontSize: 10, fontWeight: 500, color: ALERT_RED_TEXT, fontFeatureSettings: TABULAR }}>
              {ev.daysLate} d late
            </span>
          </div>
        );
      })}
      <div style={{ flex: 1 }} />
      <button onClick={copyChaseList} style={ghostBtn}>
        {copied === 'chase' ? 'Copied ✓' : 'Copy chase list'}
      </button>
    </div>
  ) : null;

  // ── undated strip ────────────────────────────────────────────────────
  var undatedStrip = schedule.undated.length > 0 ? (
    <div style={{ borderTop: HAIRLINE_SHELL }}>
      <div onClick={function() { setUndatedOpen(!undatedOpen); }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', cursor: 'pointer' }}>
        <span style={eyebrow}>
          Can't be projected · {schedule.undated.length} item{schedule.undated.length === 1 ? '' : 's'}
        </span>
        <span style={{ fontSize: 9, color: INK_3 }}>{undatedOpen ? '▾' : '▸'}</span>
      </div>
      {undatedOpen && schedule.undated.map(function(u) {
        var it = u.item;
        return (
          <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderTop: HAIRLINE_ROW }}>
            <Dot color={ZONE_GREY} />
            <span style={{ fontSize: 11, fontWeight: 500, color: INK_1 }}>{it.productName || it.item}</span>
            <span style={{ fontSize: 10, color: INK_3 }}>
              {it.vendor || '—'}{it.poNumber ? ' · ' + it.poNumber : ''} · {it.projectLabel || it.project || '—'}
            </span>
            <span style={Object.assign({}, eyebrow, { fontSize: 9 })}>
              {u.reason === 'no_order_date' ? 'No order date' : 'No lead time'}
            </span>
            <div style={{ flex: 1 }} />
            {onNavigate && (
              <span onClick={function() { onNavigate('/items/' + it.id); }}
                style={{ fontSize: 11, fontWeight: 500, color: INK_1, cursor: 'pointer',
                  textDecoration: 'underline', textDecorationColor: '#D8D7D2' }}>
                {u.reason === 'no_order_date' ? 'Add date' : 'Add lead time'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  ) : null;

  // ── selection bar ────────────────────────────────────────────────────
  var selectionBar = selection.length > 0 ? (
    <div style={{ position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)',
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: SURFACE,
      border: HAIRLINE, borderRadius: 6, boxShadow: SHADOW_MODAL, zIndex: Z_MODAL - 1 }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: INK_1, whiteSpace: 'nowrap' }}>
        {selection.length} item{selection.length === 1 ? '' : 's'} selected
      </span>
      <div style={{ width: '0.5px', height: 16, background: '#D8D7D2' }} />
      <button onClick={function() { setIsolated(!isolated); }}
        style={{ padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
          background: isolated ? INK_1 : SURFACE, color: isolated ? SURFACE : INK_1,
          border: '1px solid ' + INK_1, whiteSpace: 'nowrap' }}>
        {isolated ? 'Isolated ✓' : 'Isolate'}
      </button>
      <button onClick={clearAll} style={ghostBtn}>Clear</button>
      <div style={{ width: '0.5px', height: 16, background: '#D8D7D2' }} />
      <button onClick={copySelectionList}
        style={{ padding: '5px 14px', borderRadius: 6, fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
          background: SURFACE, color: INK_1, border: '1px solid ' + INK_1, whiteSpace: 'nowrap' }}>
        {copied === 'selection' ? 'Copied ✓' : 'Copy list for vendor email'}
      </button>
      <button onClick={exportSelectionCSV} style={ghostBtn}>Export CSV</button>
    </div>
  ) : null;

  // ── legend + footer ──────────────────────────────────────────────────
  var footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '9px 14px',
      borderTop: HAIRLINE_SHELL, fontSize: 11, color: INK_2, flexWrap: 'wrap' }}>
      <span style={{ fontFeatureSettings: TABULAR }}>
        <strong style={{ fontWeight: 500, color: INK_1 }}>{inPeriod.length}</strong> deliveries {periodNoun}
        {' · '}
        <span style={{ color: schedule.overdue.length ? ALERT_RED_TEXT : INK_2, fontWeight: schedule.overdue.length ? 500 : 400 }}>
          {schedule.overdue.length} overdue
        </span>
        {' · '}{schedule.undated.length} undated
      </span>
      <div style={{ flex: 1 }} />
      {[['onTrack', 'On track'], ['dueSoon', 'Due soon'], ['overdue', 'Overdue'], ['delivered', 'Done']].map(function(l) {
        return (
          <span key={l[0]} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: INK_3 }}>
            <Dot color={TIMING_STYLE[l[0]].dot} size={6} />{l[1]}
          </span>
        );
      })}
      <div style={{ width: '0.5px', height: 12, background: '#D8D7D2' }} />
      {[['production', 'Production'], ['transit', 'In transit'], ['receiver', 'At receiver'], ['installScheduled', 'Install sched.'], ['installed', 'Installed']].map(function(l) {
        return (
          <span key={l[0]} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: INK_3 }}>
            <LocIcon state={l[0]} color={INK_3} />{l[1]}
          </span>
        );
      })}
    </div>
  );

  // ── chrome ───────────────────────────────────────────────────────────
  var segBtn = function(v, label) {
    var active = view === v;
    return (
      <button key={v} onClick={function() { setView(v); }}
        style={{ padding: '5px 14px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
          background: SURFACE, border: 'none', borderRadius: 5,
          boxShadow: active ? 'inset 0 0 0 1.5px ' + INK_1 : 'none',
          fontWeight: active ? 500 : 400, color: active ? INK_1 : INK_2 }}>
        {label}
      </button>
    );
  };

  var statusChips = [
    ['all', 'All'], ['onorder', 'On order'], ['overdue', 'Overdue'], ['delivered', 'Delivered'],
  ];

  return (
    <div onClick={function() { setPopover(null); }}
      style={{ fontFamily: FONT_FAMILY, fontSize: 12, color: INK_1 }}>

      {/* Title row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: HAIRLINE_SHELL, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Calendar</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: INK_1, fontFeatureSettings: TABULAR }}>{periodLabel}</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'inline-flex', border: HAIRLINE, borderRadius: 6, padding: 1 }}>
          {segBtn('month', 'Month')}{segBtn('week', 'Week')}{segBtn('quarter', 'Quarter')}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button onClick={function() { stepPeriod(-1); }} style={ghostBtn}>‹</button>
          <button onClick={function() { setAnchor(todayIso); }}
            style={Object.assign({}, ghostBtn, { border: '0.5px solid ' + INK_1 })}>Today</button>
          <button onClick={function() { stepPeriod(1); }} style={ghostBtn}>›</button>
        </div>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderBottom: HAIRLINE_SHELL, flexWrap: 'wrap' }}>
        <button onClick={function() { setFProject(''); }}
          style={{ height: 22, padding: '0 10px', borderRadius: 11, fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
            border: fProject === '' ? '1px solid ' + INK_1 : HAIRLINE, background: SURFACE,
            fontWeight: fProject === '' ? 500 : 400, color: fProject === '' ? INK_1 : INK_2 }}>
          All projects
        </button>
        {projects.map(function(proj) {
          var active = fProject === proj.label;
          return (
            <button key={proj.id} onClick={function() { setFProject(active ? '' : proj.label); }}
              style={{ height: 22, padding: '0 10px', borderRadius: 11, fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
                border: active ? '1px solid ' + INK_1 : HAIRLINE, background: SURFACE,
                fontWeight: active ? 500 : 400, color: active ? INK_1 : INK_2 }}>
              {proj.label}
            </button>
          );
        })}
        <input value={fVendor} onChange={function(e) { setFVendor(e.target.value); }}
          placeholder="Search vendors"
          autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          style={{ height: 22, width: 190, border: HAIRLINE, borderRadius: 11, padding: '0 10px',
            fontSize: 11, fontFamily: 'inherit', color: INK_1, background: SURFACE, outline: 'none', marginLeft: 4 }} />
        <div style={{ flex: 1 }} />
        {statusChips.map(function(c) {
          var active = fStatus === c[0];
          var isOver = c[0] === 'overdue';
          return (
            <button key={c[0]} onClick={function() { setFStatus(c[0]); }}
              style={{ height: 22, padding: '0 10px', borderRadius: 11, fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
                border: active ? '1px solid ' + INK_1 : HAIRLINE, background: SURFACE,
                fontWeight: active ? 500 : 400, color: active ? INK_1 : INK_2, whiteSpace: 'nowrap' }}>
              {c[1]}
              {isOver && schedule.overdue.length > 0 && (
                <span style={{ color: ALERT_RED_TEXT, fontWeight: 500, marginLeft: 4, fontFeatureSettings: TABULAR }}>
                  {schedule.overdue.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {overdueRail}

      {view === 'month' && monthView()}
      {view === 'week' && weekView()}
      {view === 'quarter' && quarterView()}

      {undatedStrip}
      {footer}

      {qTooltip}
      {popoverEl}
      {selectionBar}
    </div>
  );
}
